import React from 'react';
import { Typography, IconButton, Collapse } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Button } from 'new/elements/Button';
import { ReactComponent as RunIcon } from 'new/components/icons/fi-rr-play.svg';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { ParameterSection } from './ParameterSection';
import { UIState } from '../hooks/useApiRequestForm';
import { Parameter, Header } from '../hooks/useParameterManager';
import styles from '../ApiRequestModal.module.scss';
import { ValidationErrors } from '../hooks/useApiRequestForm';

interface ConfigPanelProps {
  mode: 'config' | 'test';
  pathParameters: Parameter[];
  queryParameters: Parameter[];
  headers: Header[];
  rawJsonBody?: string;
  generatedSchema?: string;
  method: string;
  uiState: UIState;
  validationErrors: ValidationErrors;
  onParameterUpdate: (
    id: number,
    field: string,
    value: string | boolean,
    type: 'path' | 'query' | 'body',
  ) => void;
  onParameterRemove: (id: number, type: 'path' | 'query' | 'body') => void;
  onParameterAdd: (type: 'path' | 'query' | 'body') => void;
  onHeaderUpdate: (id: number, field: string, value: string | boolean) => void;
  onHeaderRemove: (id: number) => void;
  onHeaderAdd: () => void;
  onToggleSection: (section: keyof UIState) => void;
  onJsonBodyChange: (value: string) => void;
  onGenerateSchema: () => void;
}

export const ConfigPanel: React.FC<ConfigPanelProps> = ({
  mode,
  pathParameters,
  queryParameters,
  headers,
  rawJsonBody,
  generatedSchema,
  method,
  uiState,
  validationErrors,
  onParameterUpdate,
  onParameterRemove,
  onParameterAdd,
  onHeaderUpdate,
  onHeaderRemove,
  onHeaderAdd,
  onToggleSection,
  onJsonBodyChange,
  onGenerateSchema,
}) => {
  return (
    <div className={styles.leftPanel}>
      <div className={styles.parametersHeader}>
        <Typography variant="overline">Request Parameters</Typography>
        {/* TODO: Implement import functionality 
        <Button
          variant="text"
          className={styles.importButton}
          onClick={() => { }}>
          Import Swagger Spec
        </Button> */}
      </div>
      <ParameterSection
        mode={mode}
        title="Path Parameters"
        type="path"
        parameters={pathParameters}
        validationErrors={validationErrors.pathParameters}
        expanded={uiState.pathParamsOpen}
        onToggle={() => onToggleSection('pathParamsOpen')}
        onUpdate={onParameterUpdate}
        onRemove={onParameterRemove}
        onAdd={() => onParameterAdd('path')}
      />
      <ParameterSection
        mode={mode}
        title="Query Parameters"
        type="query"
        parameters={queryParameters}
        validationErrors={validationErrors.queryParameters}
        expanded={uiState.queryParamsOpen}
        onToggle={() => onToggleSection('queryParamsOpen')}
        onUpdate={onParameterUpdate}
        onRemove={onParameterRemove}
        onAdd={() => onParameterAdd('query')}
      />
      <ParameterSection
        mode={mode}
        title="Headers"
        type="headers"
        headers={headers}
        validationErrors={validationErrors.headers}
        expanded={uiState.headersOpen}
        onToggle={() => onToggleSection('headersOpen')}
        onUpdate={onHeaderUpdate}
        onRemove={onHeaderRemove}
        onAdd={onHeaderAdd}
      />
      {method !== 'GET' && (
        <div>
          <div className={styles.sectionHeader} onClick={() => onToggleSection('bodyOpen')}>
            <IconButton
              size="small"
              className={`${styles.expandIcon} ${uiState.bodyOpen ? styles.expanded : ''}`}
            >
              <ExpandMoreIcon />
            </IconButton>
            <Typography variant="caption">Request Body</Typography>
          </div>
          <Collapse in={uiState.bodyOpen}>
            <div className={styles.sectionContent}>
              <div className={styles.bodyEditorsContainer}>
                <div className={styles.editorColumn}>
                  <div className={styles.editorHeader}>
                    <Typography variant="overline">PASTE JSON SAMPLE</Typography>
                    <div className={styles.generateButton}>
                      <Typography variant="body2">Generate</Typography>
                      <IconButton
                        size="small"
                        onClick={onGenerateSchema}
                        disabled={mode === 'test'}
                      >
                        <RunIcon />
                      </IconButton>
                    </div>
                  </div>
                  <div className={styles.editorWrapper}>
                    <LoadableMonacoEditor
                      language="json"
                      theme="dark"
                      value={rawJsonBody}
                      height={300}
                      onChange={(value) => onJsonBodyChange(value || '')}
                      options={{
                        readOnly: mode === 'test',
                        lineNumbers: 'on',
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                        formatOnType: true,
                        formatOnPaste: true,
                      }}
                    />
                  </div>
                </div>
                <div className={styles.editorColumn}>
                  <div className={styles.editorHeader}>
                    <Typography variant="overline">GENERATED SCHEMA</Typography>
                  </div>
                  <div className={styles.editorWrapper}>
                    <LoadableMonacoEditor
                      language="json"
                      theme="dark"
                      value={generatedSchema}
                      height={300}
                      onChange={() => {}}
                      options={{
                        readOnly: true,
                        lineNumbers: 'on',
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};
